import { CaretLeft, X } from "@phosphor-icons/react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { clsx } from "clsx";
import {
  type ComponentPropsWithoutRef,
  type ElementRef,
  forwardRef,
  type HTMLAttributes,
  useEffect,
} from "react";

import { Button } from "common/components/ui/Button";

interface DialogProps
  extends ComponentPropsWithoutRef<typeof DialogPrimitive.Root> {
  onAfterClose?: () => void;
}

const dialogCloseDuration = 300;

function Dialog({
  onAfterClose = null,
  onOpenChange,
  open,
  ...props
}: DialogProps) {
  const isControlled = open !== undefined;

  useEffect(() => {
    if (isControlled && !open && onAfterClose) {
      const timeoutID = setTimeout(() => onAfterClose(), dialogCloseDuration);
      return () => clearTimeout(timeoutID);
    }
  }, [open]);

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(open) => {
        if (onOpenChange) {
          onOpenChange(open);
        }
        if (!isControlled && !open && onAfterClose) {
          setTimeout(() => onAfterClose(), dialogCloseDuration);
        }
      }}
      {...props}
    />
  );
}
Dialog.displayName = DialogPrimitive.Root.displayName;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = DialogPrimitive.Overlay;

const dialogWidthClassNames = {
  md: "w-112",
  lg: "w-135",
  xl: "w-225",
} as const;

interface DialogContentProps
  extends ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  size?: keyof typeof dialogWidthClassNames;
}

function isThirdPartyModalOpen() {
  return !!document.getElementById("Cardinal-ElementContainer");
}

const DialogContent = forwardRef<
  ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(
  (
    {
      className,
      children,
      size = "md",
      onInteractOutside,
      onEscapeKeyDown,
      ...props
    },
    ref,
  ) => (
    <DialogPortal>
      <DialogOverlay
        onClick={(e) => e.stopPropagation()}
        className="fixed inset-0 z-overlay bg-black-950/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
      />
      <DialogPrimitive.Content
        ref={ref}
        onClick={(e) => e.stopPropagation()}
        className={clsx(
          "transition-none max-h-9/10 fixed overflow-auto flex flex-col left-1/2 top-1/2 z-dialog -translate-x-1/2 -translate-y-1/2 bg-white shadow-lg duration-200 focus:outline-none",
          "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          className,
          dialogWidthClassNames[size],
          size === "xl" ? "rounded-4xl" : "pt-4 px-4 pb-6 rounded-5xl",
        )}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          (e.target as HTMLDivElement).focus();
        }}
        onInteractOutside={(e) => {
          if (isThirdPartyModalOpen()) e.preventDefault();

          onInteractOutside?.(e);
        }}
        onEscapeKeyDown={(e) => {
          if (isThirdPartyModalOpen()) e.preventDefault();

          onEscapeKeyDown?.(e);
        }}
        {...props}
      >
        {children}
        <Button asChild size="sm" variant="ghost-black" intent="iconOnly">
          <DialogClose
            id="close-button"
            className={clsx(
              "absolute",
              size === "xl" ? "top-2 right-2" : "right-4 top-4",
            )}
          >
            <X size={20} />
            <span className="sr-only">Close</span>
          </DialogClose>
        </Button>
      </DialogPrimitive.Content>
    </DialogPortal>
  ),
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const OverlayDialogContent = forwardRef<
  ElementRef<typeof DialogPrimitive.Content>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogPrimitive.Overlay />
    <DialogPrimitive.Content
      ref={ref}
      className={clsx(
        "fixed flex flex-col inset-x-0 top-0 z-dialog bg-white duration-200 focus:outline-none h-dvh overflow-y-auto",
        className,
      )}
      onOpenAutoFocus={(e) => {
        e.preventDefault();
        (e.target as HTMLDivElement).focus();
      }}
      {...props}
    >
      {children}
      <Button asChild variant="secondary-black" intent="iconOnly">
        <DialogClose id="close-button" className="absolute left-4 top-3">
          <CaretLeft size={20} />
          <span className="sr-only">Close</span>
        </DialogClose>
      </Button>
    </DialogPrimitive.Content>
  </DialogPortal>
));
OverlayDialogContent.displayName = "OverlayDialogContent";

function OverlayDialogTitle({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <DialogPrimitive.Title
      className={clsx("px-14 py-4 text-center text-xl-bold", className)}
      {...props}
    >
      {children}
    </DialogPrimitive.Title>
  );
}
function DialogFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={clsx(
        "flex justify-between bg-white p-4 -mb-6 -mx-4 rounded-b-3xl border-t border-t-black-200",
        className,
      )}
      {...props}
    />
  );
}
DialogFooter.displayName = "DialogFooter";

function DialogTitle({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <DialogPrimitive.Title
      className={clsx("px-10 text-center text-xl-bold", className)}
      {...props}
    >
      {children}
    </DialogPrimitive.Title>
  );
}

function DialogDescription({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <DialogPrimitive.Description
      className={clsx(
        "mt-2 px-10 text-center text-sm text-black-500",
        className,
      )}
      {...props}
    >
      {children}
    </DialogPrimitive.Description>
  );
}

function DialogHeader({ children }: { children: string }) {
  return (
    <div className="flex border-b py-3.5 px-4 border-b-black-100">
      <DialogPrimitive.Title className="text-md-bold">
        {children}
      </DialogPrimitive.Title>
    </div>
  );
}

export {
  Dialog,
  DialogTrigger,
  DialogPortal,
  DialogOverlay,
  DialogContent,
  DialogClose,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogHeader,
  OverlayDialogContent,
  OverlayDialogTitle,
};
